import type { WppTicketMessage } from '../types/whatsappTickets'

export interface FarmaTicketTiming {
  intervaloMedioRespostaMinutos: number | null
  tempoConclusaoMinutos: number | null
}

type Speaker = 'cliente' | 'atendente'

function round1(value: number): number {
  return Math.round(value * 10) / 10
}

/** Converte minutos decimais em `HH:MM:SS`. */
export function formatMinutesAsHms(minutos: number | null | undefined): string {
  if (minutos == null || !Number.isFinite(minutos) || minutos < 0) return '—'
  const totalSec = Math.round(minutos * 60)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${pad(h)}:${pad(m)}:${pad(s)}`
}

function toMs(datetime: string | null | undefined): number | null {
  if (!datetime) return null
  const ms = new Date(datetime).getTime()
  return Number.isFinite(ms) ? ms : null
}

/** `sent` = atendente; demais tipos com timestamp válido = cliente. */
function speakerOf(msg: WppTicketMessage): Speaker | null {
  if (!toMs(msg.datetime)) return null
  return (msg.type || '').toLowerCase() === 'sent' ? 'atendente' : 'cliente'
}

/**
 * Intervalo médio pergunta–resposta (minutos, 1 casa).
 * Pares cliente→atendente; bloco contínuo do cliente conta 1 par
 * (1ª msg do bloco → 1ª resposta do atendente).
 */
export function calcIntervaloMedioRespostaMinutos(messages: WppTicketMessage[]): number | null {
  const ordered = [...messages].sort(
    (a, b) => (toMs(a.datetime) ?? 0) - (toMs(b.datetime) ?? 0),
  )

  const gaps: number[] = []
  let clientBlockStartMs: number | null = null

  for (const msg of ordered) {
    const speaker = speakerOf(msg)
    const ms = toMs(msg.datetime)
    if (!speaker || ms == null) continue

    if (speaker === 'cliente') {
      if (clientBlockStartMs == null) clientBlockStartMs = ms
      continue
    }

    if (clientBlockStartMs != null) {
      const gapMin = (ms - clientBlockStartMs) / 60_000
      if (gapMin >= 0) gaps.push(gapMin)
      clientBlockStartMs = null
    }
  }

  if (gaps.length === 0) return null
  return round1(gaps.reduce((acc, v) => acc + v, 0) / gaps.length)
}

/** Tempo da 1ª à última mensagem (≥ 2 msgs válidas). */
export function calcTempoConclusaoMinutos(messages: WppTicketMessage[]): number | null {
  const times = messages
    .map((m) => toMs(m.datetime))
    .filter((ms): ms is number => ms != null)
    .sort((a, b) => a - b)

  if (times.length < 2) return null
  return round1((times[times.length - 1] - times[0]) / 60_000)
}

export function computeFarmaTicketTiming(messages: WppTicketMessage[]): FarmaTicketTiming {
  return {
    intervaloMedioRespostaMinutos: calcIntervaloMedioRespostaMinutos(messages),
    tempoConclusaoMinutos: calcTempoConclusaoMinutos(messages),
  }
}
